// src/pages/MusteriDetay.jsx
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, updateDoc, collection, addDoc, serverTimestamp, orderBy, query, onSnapshot,
  deleteDoc, getDocs, writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { uploadToCloudinary } from '../utils/cloudinary';
import {
  Copy, Check, Link2, Sliders, Image,
  X, Loader2, Mail, MapPin, StickyNote, Trash2, AlertTriangle, Plus, ArrowLeft
} from 'lucide-react';

export default function MusteriDetay({ musteriId, onBack }) {
  const navigate = useNavigate();

  const [musteri, setMusteri] = useState(null);
  const [kayitlar, setKayitlar] = useState([]);
  const [ilerleme, setIlerleme] = useState(0);
  const [kopyalandi, setKopyalandi] = useState(false);
  const [kayitForm, setKayitForm] = useState({ metin: '' });
  const [resimDosya, setResimDosya] = useState(null);
  const [resimOnizleme, setResimOnizleme] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [silModal, setSilModal] = useState(false);
  const [silYukleniyor, setSilYukleniyor] = useState(false);
  const dosyaInputRef = useRef();
  const ilerlemeTimeoutRef = useRef();

  const PUBLIC_URL = musteriId ? `${window.location.origin}/takip/${musteriId}` : null;

  useEffect(() => {
    if (!musteriId) return;
    const unsub = onSnapshot(doc(db, 'musteriler', musteriId), snap => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setMusteri(data);
        setIlerleme(data.ilerleme ?? 0);
      }
    });
    return unsub;
  }, [musteriId]);

  useEffect(() => {
    if (!musteriId) return;
    const q = query(collection(db, 'musteriler', musteriId, 'kayitlar'), orderBy('tarih', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setKayitlar(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [musteriId]);

  if (!musteri) {
    return <div className="flex items-center justify-center h-full p-8"><Loader2 className="animate-spin text-stone-600" size={28} /></div>;
  }

  function ilerlemeGuncelle(val) {
    setIlerleme(val);
    clearTimeout(ilerlemeTimeoutRef.current);
    ilerlemeTimeoutRef.current = setTimeout(async () => {
      await updateDoc(doc(db, 'musteriler', musteriId), { ilerleme: val, guncellenmeTarihi: serverTimestamp() });
    }, 600);
  }

  function linkKopyala() {
    navigator.clipboard.writeText(PUBLIC_URL);
    setKopyalandi(true);
    setTimeout(() => setKopyalandi(false), 2500);
  }

  function resimSec(e) {
    const file = e.target.files[0];
    if (!file) return;
    setResimDosya(file);
    setResimOnizleme(URL.createObjectURL(file));
  }

  async function kayitEkle(e) {
    e.preventDefault();
    if (!kayitForm.metin.trim() && !resimDosya) return;
    setYukleniyor(true);
    setUploadProgress(0);
    try {
      let resimUrl = null;
      if (resimDosya) resimUrl = await uploadToCloudinary(resimDosya, setUploadProgress);
      await addDoc(collection(db, 'musteriler', musteriId, 'kayitlar'), {
        metin: kayitForm.metin.trim(), resimUrl, tarih: serverTimestamp()
      });
      setKayitForm({ metin: '' });
      setResimDosya(null);
      setResimOnizleme(null);
      setUploadProgress(0);
    } catch (err) {
      alert('Kayıt eklenemedi: ' + err.message);
    } finally {
      setYukleniyor(false);
    }
  }

  async function musteriSil() {
    if (!musteri) return;
    setSilYukleniyor(true);
    try {
      await addDoc(collection(db, 'eskiMusteriler'), {
        isimSoyisim: musteri.isimSoyisim || '',
        telefon: musteri.telefon || '',
        isTanimi: musteri.isTanimi || '',
        silinmeTarihi: serverTimestamp(),
        olusturmaTarihi: musteri.olusturmaTarihi || null
      });
      const kayitSnap = await getDocs(collection(db, 'musteriler', musteriId, 'kayitlar'));
      const batch = writeBatch(db);
      kayitSnap.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
      await deleteDoc(doc(db, 'musteriler', musteriId));
      navigate('/admin/musteri');
    } catch (err) {
      alert('Silinemedi: ' + err.message);
    } finally {
      setSilYukleniyor(false);
      setSilModal(false);
    }
  }

  return (
    <main className="flex-1 overflow-y-auto bg-stone-950">
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={onBack} className="flex items-center gap-2 text-stone-400 hover:text-white transition-colors">
            <ArrowLeft size={20} />
            Geri
          </button>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-stone-800 flex items-center justify-center text-lg font-bold text-stone-300">
                {musteri.isimSoyisim?.charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="text-xl font-semibold text-white">{musteri.isimSoyisim}</h1>
                <div className="text-sm text-stone-500">{musteri.isTanimi || 'Aktarım'}</div>
              </div>
            </div>
            <button onClick={() => setSilModal(true)} className="flex items-center gap-1.5 text-xs text-stone-600 hover:text-red-400 transition-colors border border-stone-800 hover:border-red-400/30 px-3 py-1.5 rounded-lg">
              <Trash2 size={13} /> Sil
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {musteri.email && <div className="flex items-center gap-2 text-sm text-stone-400">
              <Mail size={14} className="text-stone-600" />
              {musteri.email}
            </div>}
            {musteri.adres && <div className="flex items-center gap-2 text-sm text-stone-400">
              <MapPin size={14} className="text-stone-600" />
              {musteri.adres}
            </div>}
            {musteri.notlar && <div className="flex items-start gap-2 text-sm text-stone-400 sm:col-span-2">
              <StickyNote size={14} className="text-stone-600 mt-0.5" />
              {musteri.notlar}
            </div>}
          </div>
        </div>

        <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Link2 size={16} className="text-stone-400" />
            <h2 className="font-semibold text-white text-sm">Müşteri Takip Linki</h2>
          </div>
          <div className="flex gap-2">
            <div className="flex-1 bg-stone-800 border border-stone-700 rounded-lg px-3 py-2.5 text-xs text-stone-400 font-mono truncate">
              {PUBLIC_URL}
            </div>
            <button onClick={linkKopyala} className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${kopyalandi ? 'bg-green-600/20 text-green-400 border border-green-600/30' : 'bg-stone-800 text-stone-300 border border-stone-700 hover:bg-stone-700'}`}>
              {kopyalandi ? <Check size={14} /> : <Copy size={14} />}
              {kopyalandi ? 'Kopyalandı!' : 'Kopyala'}
            </button>
          </div>
          <p className="text-xs text-stone-600 mt-2">Bu linki müşteriyle paylaşın — giriş gerektirmez.</p>
        </div>

        <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sliders size={16} className="text-stone-400" />
              <h2 className="font-semibold text-white text-sm">İş İlerlemesi</h2>
            </div>
            <span className="text-2xl font-bold text-white">{ilerleme}%</span>
          </div>
          <div className="w-full h-2 bg-stone-800 rounded-full mb-4 overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all duration-300" style={{ width: `${ilerleme}%` }} />
          </div>
          <input type="range" min="0" max="100" step="1" value={ilerleme} onChange={(e) => ilerlemeGuncelle(Number(e.target.value))} className="w-full accent-white cursor-pointer" />
          <div className="flex justify-between text-xs text-stone-600 mt-1">
            <span>0%</span>
            <span>25%</span>
            <span>50%</span>
            <span>75%</span>
            <span>100%</span>
          </div>
        </div>

        <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Plus size={16} className="text-stone-400" />
            <h2 className="font-semibold text-white text-sm">Yeni Kayıt Ekle</h2>
          </div>
          <form onSubmit={kayitEkle} className="space-y-3">
            <textarea rows="3" placeholder="Günlük not, yapılan iş, gözlem..." value={kayitForm.metin} onChange={(e) => setKayitForm({ metin: e.target.value })} className="w-full bg-stone-800 border border-stone-700 text-white px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:border-stone-500 resize-none placeholder-stone-600" />
            <div className="flex gap-2">
              <button type="button" onClick={() => dosyaInputRef.current?.click()} className="flex items-center gap-1.5 px-3 py-2 bg-stone-800 border border-stone-700 text-stone-400 rounded-lg text-sm hover:bg-stone-700 transition-colors">
                <Image size={14} />
                Resim
              </button>
              <input type="file" accept="image/*" className="hidden" ref={dosyaInputRef} onChange={resimSec} />
              <button type="submit" disabled={yukleniyor || (!kayitForm.metin.trim() && !resimDosya)} className="flex-1 flex items-center justify-center gap-1.5 bg-white text-stone-900 font-semibold py-2 rounded-lg text-sm hover:bg-stone-200 transition-colors disabled:opacity-40">
                <Plus size={14} />
                Kayıt Ekle
              </button>
            </div>
          </form>
        </div>

        <div>
          <h2 className="text-sm font-semibold text-stone-400 tracking-widest uppercase mb-4">Kayıtlar ({kayitlar.length})</h2>
          {kayitlar.length === 0 ? (
            <div className="text-center text-stone-600 text-sm py-10 bg-stone-900 border border-stone-800 rounded-xl">
              Henüz kayıt eklenmedi
            </div>
          ) : (
            <div className="space-y-3">
              {kayitlar.map((kayit) => (
                <div key={kayit.id} className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                  <div className="text-sm text-stone-200 leading-relaxed mb-3 whitespace-pre-wrap">{kayit.metin}</div>
                  {kayit.resimUrl && (
                    <img src={kayit.resimUrl} alt="Kayıt" className="rounded-xl max-h-48 w-full object-cover border border-stone-700" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sil Modal */}
      {silModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-stone-900 border border-stone-800 rounded-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                <AlertTriangle size={20} className="text-red-400" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-white">Müşteriyi Sil</h3>
                <p className="text-sm text-stone-400 mt-1">Bu işlem geri alınamaz</p>
              </div>
            </div>
            <div className="bg-stone-800/50 rounded-xl p-4 mb-6 text-sm space-y-2">
              <p><span className="text-emerald-400">✓</span> Kayıtlar ve fotoğraflar silinecek</p>
              <p><span className="text-emerald-400">✓</span> Bilgiler arşive taşınacak</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setSilModal(false)} disabled={silYukleniyor} className="flex-1 bg-stone-800 text-stone-300 py-3 rounded-xl text-sm font-medium hover:bg-stone-700 transition-all">
                İptal
              </button>
              <button onClick={musteriSil} disabled={silYukleniyor} className="flex-1 flex items-center justify-center gap-2 bg-red-500 text-white py-3 rounded-xl text-sm font-semibold hover:bg-red-600 transition-all shadow-lg disabled:opacity-50">
                {silYukleniyor ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                {silYukleniyor ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

